import mixpanel, { type Callback, type Dict, type RequestOptions } from 'mixpanel-browser';

import { coerceMixpanelShopMetrics } from '@/helpers/analytics/mixpanel-metrics';
import { getAppContext } from '@/helpers/get-app-context';

const currentVersion = import.meta.env.VITE_APP_VERSION as string | undefined;
const VALID_MIXPANEL_PLAN_IDS = new Set([0, 1, 2, 6]);
const MIXPANEL_PLAN_NAMES: Record<number, string> = { 0: 'Free', 1: 'Pro', 2: 'Basic', 6: 'Expert' };
const PERSONAL_PROPERTIES = new Set(['name', 'email', 'fullname', 'firstname', 'lastname', 'username']);

function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizeValue);
  if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return sanitizeAnalyticsProperties(value as Dict);
  }
  return value;
}

// Preserve analytics data while excluding name and email fields.
export function sanitizeAnalyticsProperties(properties: Dict = {}): Dict {
  const result: Dict = {};
  for (const [key, value] of Object.entries(properties)) {
    const normalized = key.toLowerCase().replace(/[^a-z]/g, '');
    if (!PERSONAL_PROPERTIES.has(normalized)) result[key] = sanitizeValue(value);
  }
  return result;
}

function normalizeMixpanelPlanSuperProps(props: Dict | undefined, fallbackPlanId?: unknown): Dict {
  const raw = props?.['Plan ID'] ?? fallbackPlanId;
  const num = typeof raw === 'number' ? raw : Number(raw);
  const planId = Number.isFinite(num) && VALID_MIXPANEL_PLAN_IDS.has(num) ? num : 0;
  return { 'Plan ID': planId, 'Plan Name': MIXPANEL_PLAN_NAMES[planId] ?? 'Free' };
}

let mixpanelInitialized = false;

export function initialize() {
  if (/^\/reset-password(?:\/|$)/.test(window.location.pathname)) return;
  mixpanel.init(import.meta.env.VITE_APP_MIXPANEL_PROJECT_TOKEN, {
    batch_requests: false,
    cross_subdomain_cookie: true,
    debug: false,
    persistence: 'cookie',
    secure_cookie: true,
    track_pageview: false,
    property_blacklist: ['$name', '$email', 'name', 'email', 'Name', 'Email'],
  });

  const user = window.__DATA__?.user;
  if (user) {
    mixpanel.identify(String(user.member_id));
    const serverProps = { ...(getAppContext().MIXPANEL_PROPERTIES ?? {}) };
    const planProps = normalizeMixpanelPlanSuperProps(serverProps, user.plan_id);
    const merged = sanitizeAnalyticsProperties(coerceMixpanelShopMetrics({ ...serverProps, ...planProps }));
    mixpanel.people.set(merged);
    mixpanel.register(merged);
  } else {
    mixpanel.register(normalizeMixpanelPlanSuperProps(undefined));
  }
  mixpanelInitialized = true;
}

export function startSessionRecording() {
  if (!mixpanelInitialized) return;
  mixpanel.set_config({
    record_sessions_percent: 50,
    record_mask_text_selector: '*',
    record_block_selector: 'img, video, audio, iframe, canvas, svg, input, textarea, select, [contenteditable]',
  });
  mixpanel.start_session_recording();
}

export function stopSessionRecording() {
  if (mixpanelInitialized) mixpanel.stop_session_recording();
}

export function reset() {
  if (mixpanelInitialized) mixpanel.reset();
}

export function track(
  eventName: string,
  properties?: Dict,
  optionsOrCallback?: RequestOptions | Callback,
  callback?: Callback,
) {
  if (!mixpanelInitialized) return;
  mixpanel.track(eventName, sanitizeAnalyticsProperties({ appVersion: currentVersion, ...properties }), optionsOrCallback, callback);
}

export function appendUserProperties(appendProperties: Dict) {
  if (!mixpanelInitialized) return;
  const properties = sanitizeAnalyticsProperties(appendProperties);
  if (Object.keys(properties).length > 0) mixpanel.people.append(properties);
}

export function updateSuperProperties(properties: Dict) {
  if (!mixpanelInitialized) return;
  const normalized = sanitizeAnalyticsProperties(coerceMixpanelShopMetrics(properties));
  if (Object.keys(normalized).length > 0) {
    mixpanel.register(normalized);
    mixpanel.people.set(normalized);
  }
}

export function syncMixpanelPlanFromUser(planId?: unknown) {
  if (!mixpanelInitialized) return;
  const properties = normalizeMixpanelPlanSuperProps(undefined, planId ?? window.__DATA__?.user?.plan_id);
  mixpanel.register(properties);
  mixpanel.people.set(properties);
}
